import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { GalactusCoreDTO } from '../../shared/models/galactus-core-dto';
import { ChunkErrorRecoveryService, isChunkLoadError } from './chunk-error-recovery.service';
import { NotificationService } from './notification.service';

/** Testo mostrato per status quando la risposta non porta alcun messaggio del server. */
const HTTP_STATUS_KEYS: Record<number, string> = {
  0: 'common.error.http.network',
  400: 'common.error.http.badRequest',
  401: 'common.error.http.unauthorized',
  403: 'common.error.forbidden',
  404: 'common.error.http.notFound',
  408: 'common.error.http.timeout',
  409: 'common.error.http.conflict',
  413: 'common.error.http.tooLarge',
  422: 'common.error.http.unprocessable',
  429: 'common.error.http.tooManyRequests',
  502: 'common.error.http.unavailable',
  503: 'common.error.http.unavailable',
  504: 'common.error.http.timeout',
};

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService extends ErrorHandler {

  constructor(
    private notificationsService: NotificationService,
    private translateService: TranslateService,
    private chunkErrorRecovery: ChunkErrorRecoveryService,
  ) {
    super();
  }

  override handleError(error: Error | HttpErrorResponse) {
    if (isChunkLoadError(error)) {
      this.chunkErrorRecovery.tryReloadOrNotify();
      return;
    }

    let displayMessage = error?.message || this.translateService.instant('common.error.unhandled');

    if (error instanceof HttpErrorResponse) {
      // BE invia GalactusCoreDTO localizzato in base ad Accept-Language: privilegia errorMessage,
      // quindi cade sui fallback storici per compat con risposte non standard. Senza alcun messaggio
      // dal server (proxy 502, rete giù, risposta fuori dall'advice) mostra un testo tradotto per
      // status: mai il "Http failure response for ..." di Angular, né un oggetto o una pagina HTML.
      displayMessage = this.serverMessage(error.error) || this.translateService.instant(this.statusKey(error.status));
    }

    if (!environment.production) {
      displayMessage += ' ' + this.translateService.instant('common.error.seeConsole');
    }

    this.notificationsService.error((error as any)?.error?.message?.includes('uni_order_cat') ? this.translateService.instant('common.error.orderNumberType') : displayMessage );
    //FIXME K3 provvisorio
    //this.store.dispatch(new BaseAction(LayoutActionType.Loading, false));

    super.handleError(error);
  }

  /** Il messaggio che il server ha scritto per l'utente, o null se la risposta non ne porta uno. */
  private serverMessage(body: unknown): string | null {
    if (typeof body === 'string') {
      // Una pagina d'errore HTML (proxy, gateway) non è un messaggio per l'utente.
      const text = body.trim();
      return text && !text.startsWith('<') ? text : null;
    }
    // Oltre a GalactusCoreDTO, le forme storiche: errore annidato, e il {message, error} di Spring.
    const dto = body as (GalactusCoreDTO & { message?: unknown; error?: { errorMessage?: unknown } | string }) | null;
    const nested = typeof dto?.error === 'object' ? dto.error?.errorMessage : undefined;
    const candidates = [dto?.errorMessage, nested, dto?.message, dto?.error];
    return candidates.find((c): c is string => typeof c === 'string' && c.trim() !== '') ?? null;
  }

  private statusKey(status: number): string {
    if (HTTP_STATUS_KEYS[status]) {
      return HTTP_STATUS_KEYS[status];
    }
    if (status >= 400 && status < 500) {
      return 'common.error.http.client';
    }
    if (status >= 500) {
      return 'common.error.http.server';
    }
    return 'common.error.unhandled';
  }
}
